/**
 * i18n des fiches véhicule (`/location/vehicules/<id>` · `/en/...`).
 *
 * Chrome (libellés) + maps de valeurs data (features, couleurs intérieures,
 * motorisation). FR et EN rendent les mêmes composants via `locale` ; défaut
 * `fr` → zéro régression. Voir VehicleFicheView.
 *
 * Couleurs EXTÉRIEURES : noms constructeur (Rosso Corsa, Bleu Portimao…) →
 * conservés tels quels (usage courant). Couleurs INTÉRIEURES « Cuir X » →
 * traduites (« X leather »). Téléphone EN permissif (leads internationaux).
 */
import type { Locale } from "@/config/i18n";
import { type I18n } from "@/config/localeText";
import { PHONE_PATTERNS } from "@/config/forms";

/** Caractéristiques (toutes les valeurs distinctes du catalogue). */
const FEATURE_EN: Record<string, string> = {
  "Jantes en alliage 19 pouces": "19-inch alloy wheels",
  "Phares LED adaptatifs": "Adaptive LED headlights",
  "Toit ouvrant panoramique": "Panoramic sunroof",
  "Sièges sport en cuir": "Leather sport seats",
  "Système audio premium": "Premium audio system",
  "Climatisation automatique 4 zones": "4-zone automatic climate control",
  "Freinage d'urgence automatique": "Automatic emergency braking",
  "Régulateur de vitesse adaptatif": "Adaptive cruise control",
  "Surveillance des angles morts": "Blind-spot monitoring",
  "Connectivité Bluetooth": "Bluetooth connectivity",
  "Navigation GPS": "GPS navigation",
  "Caméra de recul": "Rear-view camera",
  "Sièges massants": "Massage seats",
  "Bar réfrigéré": "Refrigerated bar",
  "Cave à champagne": "Champagne cellar",
  "Ciel de toit étoilé": "Starlight headliner",
  "Configuration 4 places VIP": "4-seat VIP configuration",
  "Configuration 7 places luxe": "7-seat luxury configuration",
  "Kit carrosserie BRABUS": "BRABUS body kit",
  "Rideaux électriques": "Electric curtains",
  Réfrigérateur: "Refrigerator",
  "Tables escamotables": "Folding tables",
  "Échappement BRABUS": "BRABUS exhaust",
  "Écrans individuels": "Individual screens",
};

/** Couleurs intérieures « Cuir X ». */
const COLOR_EN: Record<string, string> = {
  "Cuir beige": "Beige leather",
  "Cuir blanc": "White leather",
  "Cuir bordeaux": "Burgundy leather",
  "Cuir brun": "Brown leather",
  "Cuir cognac": "Cognac leather",
  "Cuir ivoire": "Ivory leather",
  "Cuir marron": "Brown leather",
  "Cuir nappa beige": "Beige Nappa leather",
  "Cuir nappa noir": "Black Nappa leather",
  "Cuir noir et orange": "Black and orange leather",
  "Cuir noir": "Black leather",
  "Cuir rouge": "Red leather",
};

export const localizeFeatures = (items: string[], locale: Locale): string[] =>
  locale === "en" ? items.map((i) => FEATURE_EN[i] ?? i) : items;

export const localizeColor = (value: string, locale: Locale): string =>
  locale === "en" ? (COLOR_EN[value] ?? value) : value;

/** Motorisation : seul le mot « cylindres » est français. */
export const localizeEngine = (value: string, locale: Locale): string =>
  locale === "en" ? value.replace(/cylindres/gi, "cylinders") : value;

// Motifs téléphone centralisés (@/config/forms) — mêmes RegExp qu'avant.
const FR_PHONE = PHONE_PATTERNS.fr;
const INTL_PHONE = PHONE_PATTERNS.intl;

export type FicheUi = {
  page: {
    crumbHome: string;
    crumbRental: string;
    crumbVehicles: string;
    book: string;
    relatedHeading: string;
    addChauffeur: string;
    organiseExcursion: string;
  };
  presentation: {
    back: string;
    seePhoto: string;
    openGallery: string;
    allPhotos: string;
    photosCount: (n: number) => string;
    seeMainPhoto: string;
    mainView: string;
    altView: string;
    year: string;
    mileage: string;
    gearbox: string;
    fuel: string;
    indicativeRate: string;
    kmPerDay: (km: number) => string;
    perDay: string;
    order: string;
    closeGallery: string;
    prevPhoto: string;
    nextPhoto: string;
    goToPhoto: (n: number) => string;
    photoOf: (i: number, total: number) => string;
    thumb: (n: number) => string;
  };
  apercu: {
    heading: string;
    subheading: string;
    engine: string;
    powerHp: string;
    powerCv: string;
    exteriorColor: string;
    interiorColor: string;
    exterior: string;
    interior: string;
    safety: string;
    other: string;
  };
  contact: {
    heading: string;
    subheading: string;
    selectedModel: string;
    perDay: string;
    priceForDuration: string;
    fullName: string;
    phone: string;
    email: string;
    vehicle: string;
    selectModel: string;
    duration: string;
    date: string;
    message: string;
    submit: string;
    sending: string;
    error: string;
    successToast: string;
    close: string;
    v: {
      nameReq: string;
      nameInvalid: string;
      phoneReq: string;
      phoneInvalid: string;
      emailReq: string;
      emailInvalid: string;
      vehicleReq: string;
      durationReq: string;
      messageReq: string;
      messageShort: string;
      messageLong: string;
    };
    phonePattern: RegExp;
    subjectPrefix: string;
  };
  reco: { heading: string; subheading: string };
};

export const FICHE_UI: I18n<FicheUi> = {
  fr: {
    page: {
      crumbHome: "Accueil",
      crumbRental: "Location",
      crumbVehicles: "Véhicules",
      book: "Réserver ce véhicule",
      relatedHeading: "Services liés à ce véhicule",
      addChauffeur: "Ajouter un chauffeur privé",
      organiseExcursion: "Organiser une excursion sur mesure",
    },
    presentation: {
      back: "Retour en arrière",
      seePhoto: "Voir la photo",
      openGallery: "Ouvrir la galerie",
      allPhotos: "Voir toutes les photos",
      photosCount: (n) => `${n} photos`,
      seeMainPhoto: "Voir la photo principale",
      mainView: "Vue principale",
      altView: "Vue alternative",
      year: "Année",
      mileage: "Kilométrage",
      gearbox: "Boîte de vitesse",
      fuel: "Carburant",
      indicativeRate: "Tarif indicatif selon disponibilite et periode.",
      kmPerDay: (km) => `${km} km inclus par jour.`,
      perDay: "/jour",
      order: "COMMANDER",
      closeGallery: "Fermer la galerie",
      prevPhoto: "Photo précédente",
      nextPhoto: "Photo suivante",
      goToPhoto: (n) => `Aller à la photo ${n}`,
      photoOf: (i, total) => `Photo ${i}/${total}`,
      thumb: (n) => `Miniature ${n}`,
    },
    apercu: {
      heading: "Aperçu détaillé",
      subheading: "Extérieur, intérieur, sécurité et autres caractéristiques",
      engine: "Motorisation",
      powerHp: "Puissance (ch)",
      powerCv: "Puissance (cv)",
      exteriorColor: "Couleur extérieur",
      interiorColor: "Couleur intérieur",
      exterior: "Extérieur Et Châssis",
      interior: "Intérieur",
      safety: "Sécurité",
      other: "Autre",
    },
    contact: {
      heading: "Finaliser votre location",
      subheading:
        "Formulaire court : modele preselectionne, date souhaitee, confirmation rapide par notre equipe.",
      selectedModel: "Modele selectionne :",
      perDay: "/jour",
      priceForDuration: "Prix retenu pour la duree choisie :",
      fullName: "Nom complet",
      phone: "Telephone",
      email: "Email",
      vehicle: "Vehicule souhaite",
      selectModel: "Selectionner un modele",
      duration: "Duree de location",
      date: "Date souhaitee (optionnelle)",
      message: "Message",
      submit: "Envoyer ma demande de location",
      sending: "Envoi en cours...",
      error: "Impossible d'envoyer la demande pour le moment.",
      successToast:
        "Demande envoyee. Nous revenons vite avec disponibilites et suite.",
      close: "Fermer",
      v: {
        nameReq: "Le nom est requis",
        nameInvalid: "Nom invalide",
        phoneReq: "Le telephone est requis",
        phoneInvalid: "Numero invalide",
        emailReq: "L'email est requis",
        emailInvalid: "Email invalide",
        vehicleReq: "Le modele est requis",
        durationReq: "La duree est requise",
        messageReq: "Le message est requis",
        messageShort: "Message trop court (min. 10 caracteres)",
        messageLong: "Message trop long (max. 1000 caracteres)",
      },
      phonePattern: FR_PHONE,
      subjectPrefix: "Demande location - ",
    },
    reco: {
      heading: "Vous pourriez également être intéressé par",
      subheading: "Autres véhicules par constructeur",
    },
  },
  en: {
    page: {
      crumbHome: "Home",
      crumbRental: "Rental",
      crumbVehicles: "Vehicles",
      book: "Book this vehicle",
      relatedHeading: "Services related to this vehicle",
      addChauffeur: "Add a private chauffeur",
      organiseExcursion: "Plan a bespoke excursion",
    },
    presentation: {
      back: "Go back",
      seePhoto: "View photo",
      openGallery: "Open the gallery",
      allPhotos: "View all photos",
      photosCount: (n) => `${n} photos`,
      seeMainPhoto: "View main photo",
      mainView: "Main view",
      altView: "Alternate view",
      year: "Year",
      mileage: "Mileage",
      gearbox: "Gearbox",
      fuel: "Fuel",
      indicativeRate: "Indicative rate, subject to availability and season.",
      kmPerDay: (km) => `${km} km included per day.`,
      perDay: "/day",
      order: "ORDER",
      closeGallery: "Close the gallery",
      prevPhoto: "Previous photo",
      nextPhoto: "Next photo",
      goToPhoto: (n) => `Go to photo ${n}`,
      photoOf: (i, total) => `Photo ${i}/${total}`,
      thumb: (n) => `Thumbnail ${n}`,
    },
    apercu: {
      heading: "Detailed overview",
      subheading: "Exterior, interior, safety and other features",
      engine: "Engine",
      powerHp: "Power (hp)",
      powerCv: "Power (CV)",
      exteriorColor: "Exterior colour",
      interiorColor: "Interior colour",
      exterior: "Exterior & chassis",
      interior: "Interior",
      safety: "Safety",
      other: "Other",
    },
    contact: {
      heading: "Complete your rental",
      subheading:
        "Short form: preselected model, preferred date, fast confirmation from our team.",
      selectedModel: "Selected model:",
      perDay: "/day",
      priceForDuration: "Price for the chosen duration:",
      fullName: "Full name",
      phone: "Phone",
      email: "Email",
      vehicle: "Desired vehicle",
      selectModel: "Select a model",
      duration: "Rental duration",
      date: "Preferred date (optional)",
      message: "Message",
      submit: "Send my rental request",
      sending: "Sending...",
      error: "Couldn't send the request right now.",
      successToast:
        "Request sent. We'll get back quickly with availability and next steps.",
      close: "Close",
      v: {
        nameReq: "Name is required",
        nameInvalid: "Invalid name",
        phoneReq: "Phone is required",
        phoneInvalid: "Invalid number",
        emailReq: "Email is required",
        emailInvalid: "Invalid email",
        vehicleReq: "Model is required",
        durationReq: "Duration is required",
        messageReq: "Message is required",
        messageShort: "Message too short (min. 10 characters)",
        messageLong: "Message too long (max. 1000 characters)",
      },
      phonePattern: INTL_PHONE,
      subjectPrefix: "🇬🇧 [EN CLIENT] Car rental request - ",
    },
    reco: {
      heading: "You might also be interested in",
      subheading: "Other vehicles by manufacturer",
    },
  },
  ar: {
    page: {
      crumbHome: "الرئيسية",
      crumbRental: "التأجير",
      crumbVehicles: "السيارات",
      book: "احجز هذه السيارة",
      relatedHeading: "خدمات مرتبطة بهذه السيارة",
      addChauffeur: "أضف سائقاً خاصاً",
      organiseExcursion: "نظّم رحلة مصمّمة على القياس",
    },
    presentation: {
      back: "العودة",
      seePhoto: "اطّلع على الصورة",
      openGallery: "افتح المعرض",
      allPhotos: "اطّلع على جميع الصور",
      photosCount: (n) => `${n} صور`,
      seeMainPhoto: "اطّلع على الصورة الرئيسية",
      mainView: "العرض الرئيسي",
      altView: "عرض بديل",
      year: "السنة",
      mileage: "المسافة المقطوعة",
      gearbox: "ناقل الحركة",
      fuel: "الوقود",
      indicativeRate: "سعر إرشادي حسب التوافر والفترة.",
      kmPerDay: (km) => `${km} كم مشمولة يومياً.`,
      perDay: "/يوم",
      order: "اطلب الآن",
      closeGallery: "إغلاق المعرض",
      prevPhoto: "الصورة السابقة",
      nextPhoto: "الصورة التالية",
      goToPhoto: (n) => `الانتقال إلى الصورة ${n}`,
      photoOf: (i, total) => `صورة ${i}/${total}`,
      thumb: (n) => `مصغّرة ${n}`,
    },
    apercu: {
      heading: "نظرة تفصيلية",
      subheading: "الخارج والداخل والسلامة وخصائص أخرى",
      engine: "المحرّك",
      powerHp: "القدرة (حصان)",
      powerCv: "القدرة (حصان ضريبي)",
      exteriorColor: "اللون الخارجي",
      interiorColor: "اللون الداخلي",
      exterior: "الخارج والهيكل",
      interior: "الداخل",
      safety: "السلامة",
      other: "أخرى",
    },
    contact: {
      heading: "أكمل تأجيرك",
      subheading:
        "نموذج قصير: طراز محدّد مسبقاً، التاريخ المرغوب، تأكيد سريع من فريقنا.",
      selectedModel: "الطراز المختار:",
      perDay: "/يوم",
      priceForDuration: "السعر المعتمد للمدّة المختارة:",
      fullName: "الاسم الكامل",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      vehicle: "السيارة المرغوبة",
      selectModel: "اختر طرازاً",
      duration: "مدّة التأجير",
      date: "التاريخ المرغوب (اختياري)",
      message: "الرسالة",
      submit: "أرسل طلب التأجير",
      sending: "جارٍ الإرسال...",
      error: "تعذّر إرسال الطلب في الوقت الحالي.",
      successToast:
        "تم إرسال الطلب. سنعاود التواصل بسرعة بالتوافر والخطوات التالية.",
      close: "إغلاق",
      v: {
        nameReq: "الاسم مطلوب",
        nameInvalid: "اسم غير صالح",
        phoneReq: "الهاتف مطلوب",
        phoneInvalid: "رقم غير صالح",
        emailReq: "البريد الإلكتروني مطلوب",
        emailInvalid: "بريد إلكتروني غير صالح",
        vehicleReq: "الطراز مطلوب",
        durationReq: "المدّة مطلوبة",
        messageReq: "الرسالة مطلوبة",
        messageShort: "الرسالة قصيرة جداً (10 أحرف على الأقل)",
        messageLong: "الرسالة طويلة جداً (1000 حرف كحدّ أقصى)",
      },
      phonePattern: INTL_PHONE,
      subjectPrefix: "🇸🇦 [AR CLIENT] طلب تأجير - ",
    },
    reco: {
      heading: "قد يهمّك أيضاً",
      subheading: "سيارات أخرى حسب الصانع",
    },
  },
};
